/**
 * <VisitGuidanceSection> — §6 of <ZoomPanel>: the Risks & Gaps agent's
 * `followup-visit-topics` special (PD-1379) rendered as dismissible cards for
 * the treating physician during the call. Rides the report's full lifecycle
 * via `usePatientRisksGaps` (stored topics paint fast; a stale or missing
 * report auto-kicks one rebuild — the server refuses concurrent builds, so a
 * simultaneously open Risks & Gaps chart tab is race-safe).
 *
 * Dismissals live in <ZoomPanel> (`hiddenTopics`), scoped to one report id:
 * topic ids are positional per report, so a new report landing resets them.
 */
import { useEffect, useMemo } from 'react'
import { ActionIcon, Button, Group, Loader } from '@mantine/core'
import { IconBulb, IconChevronDown, IconChevronUp } from '@tabler/icons-react'
import { followupVisitTopicsSpecialSchema } from '@behuman/core-contract'
import type { FollowupVisitTopicsSpecialWire } from '@behuman/core-contract'
import { usePatientRisksGaps } from '@/hooks/usePatientRisksGaps'
import { FollowupVisitTopics } from '@/pages/Assistant/components/specials/FollowupVisitTopics'
import { cn } from '@/utils/cn'
import { scribeText } from './scribeText'

export type HiddenTopics = { reportKey: string; ids: ReadonlySet<number> } | null

export type VisitGuidanceSectionProps = {
  patientId: string
  collapsed: boolean
  onToggleCollapsed: () => void
  /** Once-per-call-session guard for the forced pre-feature rebuild — owned by
      <ZoomPanel>, since this section remounts on the rail↔fullscreen switch. */
  rebuildKickedRef: { current: boolean }
  hiddenTopics: HiddenTopics
  onHideTopic: (reportKey: string, topicId: number) => void
  onRestoreTopics: () => void
}

export function VisitGuidanceSection({
  patientId,
  collapsed,
  onToggleCollapsed,
  rebuildKickedRef,
  hiddenTopics,
  onHideTopic,
  onRestoreTopics,
}: VisitGuidanceSectionProps) {
  const { data, refreshing, error, refresh, requestRefresh } = usePatientRisksGaps(patientId)

  const report = data?.risksGapsReport
  const special = useMemo<FollowupVisitTopicsSpecialWire | undefined>(() => {
    // The first special that parses: the strict schema pins the special version this build
    // knows, so a newer version stored alongside is skipped rather than misparsed.
    for (const raw of report?.artifacts.specials ?? []) {
      if (raw.type !== 'followup-visit-topics') continue
      const parsed = followupVisitTopicsSpecialSchema.safeParse(raw)
      if (parsed.success) return parsed.data
    }
    return undefined
  }, [report])

  // A report can verify up to date yet carry no (valid) special — it predates
  // the specials feature or stores a retired topic shape. One forced rebuild
  // fetches it; the guard caps it at once per call session so a build that
  // still yields none can't loop (or re-fire on remount).
  useEffect(() => {
    if (rebuildKickedRef.current || special || !report) return
    if (data?.upToDate && !data.beingSuperseded && !data.lastBuildFailed) {
      rebuildKickedRef.current = true
      void requestRefresh()
    }
  }, [data, report, special, requestRefresh, rebuildKickedRef])

  const reportKey = report?.id ?? ''
  const topics = special?.topics ?? []
  const hiddenIds = hiddenTopics?.reportKey === reportKey ? hiddenTopics.ids : undefined
  const visibleTopics = hiddenIds ? topics.filter((t) => !hiddenIds.has(t.id)) : topics
  const allHidden = topics.length > 0 && visibleTopics.length === 0

  const renderBody = () => {
    if (allHidden) {
      // A filtered-empty list must not reach the card component — it would
      // render the "no clinical concerns" empty state, which is misleading
      // when the user merely hid everything.
      return (
        <Group gap={8} align="center">
          <span className={`${scribeText.body} text-slate-500`}>All topics hidden.</span>
          <Button size="compact-xs" variant="subtle" color="gray.4" onClick={onRestoreTopics}>
            Restore
          </Button>
        </Group>
      )
    }
    if (special) {
      // The spread keeps `specialId`/`type`, which the card component's strict
      // re-narrowing requires.
      const filtered: FollowupVisitTopicsSpecialWire = { ...special, topics: visibleTopics }
      return (
        <FollowupVisitTopics
          special={filtered}
          variant="dark"
          showHideButton
          onHide={(topicId) => onHideTopic(reportKey, topicId)}
        />
      )
    }
    // Failure states render before the data-less fallbacks (a failed first GET
    // must not hide Retry behind "Loading…"). `lastBuildFailed` is durable —
    // only a forced `requestRefresh` clears it server-side.
    if (error || data?.lastBuildFailed) {
      return (
        <Group gap={8} align="center">
          <span className={`${scribeText.body} text-slate-500`}>Couldn’t load visit guidance.</span>
          <Button
            size="compact-xs"
            variant="subtle"
            color="gray.4"
            onClick={() => void (data?.lastBuildFailed ? requestRefresh() : refresh())}
          >
            Retry
          </Button>
        </Group>
      )
    }
    if (!data) {
      return <div className={`${scribeText.body} text-slate-500`}>Loading…</div>
    }
    if (!data.exists || data.beingSuperseded || refreshing) {
      return (
        <Group gap={8} align="center">
          <Loader size="xs" color="gray" />
          <span className={`${scribeText.body} text-slate-500`}>Generating visit guidance…</span>
        </Group>
      )
    }
    // Settled report with no usable special even after the one-shot forced
    // rebuild.
    return (
      <Group gap={8} align="center">
        <span className={`${scribeText.body} text-slate-500`}>No visit guidance available.</span>
        <Button
          size="compact-xs"
          variant="subtle"
          color="gray.4"
          onClick={() => void requestRefresh()}
        >
          Refresh
        </Button>
      </Group>
    )
  }

  return (
    <div
      data-testid="zoom-panel-visit-guidance"
      className={cn(
        'flex flex-col overflow-hidden border-t border-white/[0.07]',
        collapsed ? 'shrink-0' : 'min-h-0 flex-1',
      )}
    >
      <Group
        justify="space-between"
        align="center"
        wrap="nowrap"
        className="shrink-0 cursor-pointer px-[14px] py-[9px] select-none"
        onClick={onToggleCollapsed}
      >
        <Group gap={6} align="center" wrap="nowrap" className="min-w-0">
          <IconBulb size={11} style={{ color: '#a5b4fc' }} aria-hidden="true" />
          <span className={`${scribeText.eyebrow} text-slate-500`}>Visit Guidance</span>
          {topics.length > 0 && (
            <span className={`${scribeText.meta} text-slate-500`}>{visibleTopics.length}</span>
          )}
          {(refreshing || data?.beingSuperseded) && <Loader size={10} color="gray" />}
        </Group>
        <ActionIcon
          variant="subtle"
          size="xs"
          color="gray"
          aria-label={collapsed ? 'Expand visit guidance' : 'Collapse visit guidance'}
          onClick={(e) => {
            e.stopPropagation()
            onToggleCollapsed()
          }}
        >
          {collapsed ? <IconChevronDown size={14} /> : <IconChevronUp size={14} />}
        </ActionIcon>
      </Group>

      {!collapsed && (
        <div
          className={
            'min-h-0 flex-1 overflow-y-auto px-[14px] pb-[12px] ' +
            '[scrollbar-color:rgba(148,163,184,0.35)_transparent] [scrollbar-width:thin] ' +
            '[&::-webkit-scrollbar]:w-[6px] ' +
            '[&::-webkit-scrollbar-track]:bg-transparent ' +
            '[&::-webkit-scrollbar-thumb]:rounded-full ' +
            '[&::-webkit-scrollbar-thumb]:bg-slate-500/40 ' +
            '[&::-webkit-scrollbar-thumb]:transition-colors ' +
            'hover:[&::-webkit-scrollbar-thumb]:bg-slate-400/60'
          }
        >
          {renderBody()}
        </div>
      )}
    </div>
  )
}
